import { Box, InputBaseComponentProps, LinearProgress, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import ImageUploadIcon from "../../../assets/Fibo/icons/ImageUploadIcon";
import DeleteIcon from "../../../assets/images/icons/DeleteIcon";
import ImageIcon from "../../../assets/images/icons/Image/ImageIcon";
import WarningAlertIcon from "../../../assets/images/icons/WarningAlertIcon";
import { useFileDrop } from "../../../hooks/useFileDrop";
import { showErrorToast } from "../../../utils/toast";
import useImageUtils, { supportedImageFileTypes } from "../../../utils/useImageUtils";
import BriaIconButton from "../BriaIconButton/BriaIconButton";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput";
import styles from "./ImageUploader.module.scss";

type DropOverlayProps = {
	icon?: ReactNode;
	text?: string;
};

type Props = {
	src?: string;
	description?: string | ReactNode;
	supportedFilesText?: string | ReactNode;
	showPreview?: boolean;
	loading?: boolean;
	disabled?: boolean;
	inputProps?: InputBaseComponentProps;
	onUpload?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
	onDelete?: () => Promise<void>;
	className?: string;
	icon?: string | JSX.Element;
	showIcon?: boolean;
	largeIcon?: boolean;
	emptyStateClassName?: string;
	titleClassName?: string;
	descriptionClassName?: string;
	contentWrapperClassName?: string;
	maxFilesLimit?: number;
	maxFileSize?: number;
	horizontalLayout?: boolean;
	inlineError?: boolean;
	optional?: boolean;
	children?: React.ReactNode;
	showImageLimitText?: boolean;
	showUploadedImagesRow?: boolean;
	uploadedImages?: File[];
	onRemoveImage?: (index: number) => void;
	highlightError?: boolean;
	showErrorOverlay?: boolean;
	droppable?: boolean;
	dropOverlayProps?: DropOverlayProps;
	smallIcon?: boolean;
	hideSupportElement?: boolean;
	showTitle?: boolean;
	itemDescription?: string | ReactNode;
	hideDelete?: boolean;
	customOverlay?: (handlers: {
		handleUploadClick: (e: React.MouseEvent) => void;
		handleDelete: (e: React.MouseEvent<SVGElement | HTMLButtonElement>) => Promise<void>;
	}) => ReactNode;
	customOverlayClassName?: string;
	itemTitle?: string;
	loadingProgress?: number;
	onClick?: (e: React.MouseEvent) => void;
};

const ImageUploader: React.FC<Props> = ({
	src,
	description,
	supportedFilesText,
	icon,
	largeIcon,
	showPreview = true,
	loading = false,
	disabled = false,
	inputProps,
	onUpload,
	onDelete,
	className,
	emptyStateClassName,
	titleClassName,
	descriptionClassName,
	contentWrapperClassName,
	maxFilesLimit = Infinity,
	maxFileSize = Infinity,
	horizontalLayout = false,
	inlineError = false,
	optional = false,
	children,
	showImageLimitText = false,
	showUploadedImagesRow = false,
	uploadedImages = [],
	onRemoveImage,
	showIcon = true,
	highlightError = false,
	showErrorOverlay = false,
	droppable = true,
	dropOverlayProps,
	smallIcon,
	hideSupportElement = false,
	showTitle = false,
	hideDelete = false,
	itemDescription,
	customOverlay,
	customOverlayClassName,
	itemTitle,
	loadingProgress,
	onClick,
}) => {
	const defaultAcceptTypes = supportedImageFileTypes.map((type) => `image/${type}`).join(", ");
	const [blobUrl, setBlobUrl] = useState<string>();
	const [videoThumbnailUrl, setVideoThumbnailUrl] = useState<string>();
	const [uploadedFileName, setUploadedFileName] = useState<string>();
	const [error, setError] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const thumbnailUrlRef = useRef<string>();
	const { t } = useTranslation("translation", { keyPrefix: "playground" });
	const { generateVideoThumbnail } = useImageUtils();

	const isVideoUrl = useCallback((url?: string) => {
		if (!url) return false;
		const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".ogg"];
		const lowerUrl = url.toLowerCase().split("?")[0];
		return videoExtensions.some((ext) => lowerUrl.endsWith(ext));
	}, []);

	useEffect(() => {
		// Clear internal blobUrl when src prop changes externally
		setBlobUrl((prevBlobUrl) => {
			if (prevBlobUrl !== src) {
				prevBlobUrl && URL.revokeObjectURL(prevBlobUrl);

				// Extract filename from new src URL
				const filename = src ? src.split("/").pop()?.split("?")[0] : undefined;
				setUploadedFileName(filename);

				return undefined;
			}
			return prevBlobUrl;
		});

		if (!src || !isVideoUrl(src)) {
			if (thumbnailUrlRef.current) {
				URL.revokeObjectURL(thumbnailUrlRef.current);
				thumbnailUrlRef.current = undefined;
				setVideoThumbnailUrl(undefined);
			}
			return resetInput;
		}

		generateVideoThumbnail(src)
			.then((thumbnailUrl) => {
				if (thumbnailUrlRef.current && thumbnailUrlRef.current !== thumbnailUrl) {
					URL.revokeObjectURL(thumbnailUrlRef.current);
				}
				thumbnailUrlRef.current = thumbnailUrl;
				setVideoThumbnailUrl(thumbnailUrl);
			})
			.catch(() => setVideoThumbnailUrl(undefined));

		return () => {
			resetInput();
		};
	}, [src]);

	const handleUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			const files: File[] = Array.from(e.target.files || []);
			if (!files.length) return;

			if (files.length > maxFilesLimit) {
				inlineError
					? setError(t("imageToImage.filesExceedLimit"))
					: showErrorToast(t("imageToImage.filesExceedLimit", { maxFilesLimit }));
				resetInput();
				return;
			}

			const oversized = files.find((f) => f.size > maxFileSize);
			if (oversized) {
				inlineError
					? setError(t("imageToImage.fileExceedsSize"))
					: showErrorToast(t("imageToImage.fileExceedsSize"));
				resetInput();
				return;
			}

			if (file) {
				const isVideo = file.type.startsWith("video/");
				if (isVideo) {
					try {
						const thumbnailUrl = await generateVideoThumbnail(file);
						if (thumbnailUrlRef.current && thumbnailUrlRef.current !== thumbnailUrl) {
							URL.revokeObjectURL(thumbnailUrlRef.current);
						}
						thumbnailUrlRef.current = thumbnailUrl;
						setVideoThumbnailUrl(thumbnailUrl);
					} catch {
						const url = URL.createObjectURL(file);
						setBlobUrl(url);
					}
					setUploadedFileName(file.name);
				} else {
					const url = URL.createObjectURL(file);
					setBlobUrl(url);
					setUploadedFileName(file.name);
				}

				await onUpload?.(e);
			}

			resetInput();
		},
		[maxFilesLimit, maxFileSize, onUpload, generateVideoThumbnail],
	);

	const resetInput = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleDelete = useCallback(
		async (e: React.MouseEvent<SVGElement | HTMLButtonElement>) => {
			e.preventDefault();
			resetInput();

			if (blobUrl) {
				URL.revokeObjectURL(blobUrl);
				setBlobUrl(undefined);
			}
			if (thumbnailUrlRef.current) {
				URL.revokeObjectURL(thumbnailUrlRef.current);
				thumbnailUrlRef.current = undefined;
				setVideoThumbnailUrl(undefined);
			}
			setUploadedFileName(undefined);
			await onDelete?.();
		},
		[blobUrl, onDelete],
	);

	const onFileDropped = useCallback(
		async (files: File[]) => {
			if (!files.length) return;

			// Filter for accepted file types
			const acceptedTypes =
				inputProps?.accept?.split(",").map((t: string) => t.trim()) ||
				defaultAcceptTypes.split(",").map((t: string) => t.trim());
			const fileArray = files.filter((file) =>
				acceptedTypes.some(
					(type: string) => file.type === type || file.type.startsWith(type.split("/")[0] + "/"),
				),
			);

			if (!fileArray.length) return;

			// Create synthetic event to match handleUpload signature and reuse its validation logic
			const syntheticEvent = {
				target: {
					files: fileArray,
					value: "",
				},
			} as unknown as React.ChangeEvent<HTMLInputElement>;

			// Reuse handleUpload for validation and processing
			await handleUpload(syntheticEvent);
		},
		[handleUpload, inputProps, defaultAcceptTypes],
	);

	const { isDragging, dropHandlers } = useFileDrop({
		onDropped: onFileDropped,
		enabled: droppable,
		cancelOnEscape: true,
	});

	const renderFileInput = useCallback(
		() => (
			<VisuallyHiddenInput
				ref={fileInputRef}
				type="file"
				id="fileInput"
				inputProps={{ ...inputProps, accept: inputProps?.accept ?? defaultAcceptTypes }}
				onChange={handleUpload}
				disabled={disabled}
			/>
		),
		[handleUpload, inputProps, disabled, defaultAcceptTypes],
	);

	const handleUploadClick = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		resetInput();

		fileInputRef.current?.click();
	}, []);

	const supportedFiles = useMemo(() => {
		return (inputProps?.accept || defaultAcceptTypes)
			.split("image/")
			.map((type: string) => type.trim().toUpperCase())
			.join(" ");
	}, [inputProps]);

	const previewSrc = useMemo(() => {
		if (videoThumbnailUrl) return videoThumbnailUrl;
		if (blobUrl) return blobUrl;
		if (src && isVideoUrl(src)) return videoThumbnailUrl;
		return src;
	}, [videoThumbnailUrl, blobUrl, src]);

	const showImagePreview = useMemo(
		() => showPreview && (blobUrl || videoThumbnailUrl || src || loading),
		[showPreview, blobUrl, videoThumbnailUrl, src, loading],
	);

	const renderImageLimitText = () => {
		if (!showImageLimitText || maxFilesLimit === Infinity) return null;
		return (
			<Box className={styles.imageLimitText}>
				{t("imageToImage.fileLimitMbText", { size: maxFileSize / (1024 * 1024) })}
			</Box>
		);
	};

	const renderUploadedImagesRow = () => {
		if (!showUploadedImagesRow || uploadedImages.length === 0) return null;
		return (
			<Box className={styles.uploadedImagesRow}>
				{uploadedImages.map((file, index) => (
					<Box key={index} className={styles.uploadedImageThumbnail}>
						<img
							src={URL.createObjectURL(file)}
							alt={`Uploaded ${index + 1}`}
							className={styles.uploadedThumbnailImage}
						/>
						<Box
							className={styles.removeUploadedImage}
							onClick={(e) => {
								e.preventDefault();
								onRemoveImage?.(index);
							}}
						>
							×
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	const renderDragOverlay = () => {
		return (
			<Box className={clsx(styles.dragOverlay, { [styles.opaqueDragOverlay]: !blobUrl })}>
				<Box className={styles.overlayIcon}>{dropOverlayProps?.icon || <ImageUploadIcon />}</Box>
				<Typography className={styles.dragText}>
					{dropOverlayProps?.text || t("dragAndDropOverlay.dragText")}
				</Typography>
			</Box>
		);
	};

	const hasMediaPreview = blobUrl || videoThumbnailUrl || src;

	const renderUploadingOverlay = () => {
		if (!loading || !hasMediaPreview) return null;
		return (
			<Box className={clsx(styles.uploadingOverlay, customOverlayClassName)}>
				<Box className={styles.uploadingContent}>
					<LinearProgress
						variant="determinate"
						value={loadingProgress}
						className={styles.uploadProgressBar}
					/>
					{loadingProgress !== undefined && (
						<Typography className={styles.uploadProgressText}>{Math.round(loadingProgress)}%</Typography>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<Box
			className={clsx(className, styles.container)}
			component={"label"}
			sx={{ cursor: disabled ? "not-allowed" : "pointer" }}
			onClick={onClick}
			{...(droppable && dropHandlers)}
		>
			{isDragging && renderDragOverlay()}
			{renderFileInput()}
			{!horizontalLayout && showImagePreview ? (
				<>
					<Box className={styles.imgContainer}>
						{!hideDelete && !customOverlay && !loading && (
							<DeleteIcon className={styles.deleteIcon} onClick={handleDelete} />
						)}
						<LazyLoadImage
							className={styles.img}
							src={previewSrc}
							loading="lazy"
							threshold={100}
							effect="blur"
						/>
						{showErrorOverlay && (
							<Box className={styles.errorOverlay}>
								<WarningAlertIcon width={40} height={40} />
							</Box>
						)}
						{renderUploadingOverlay()}
						{customOverlay && !loading && (
							<Box className={clsx(styles.customOverlay, customOverlayClassName)}>
								{customOverlay({ handleUploadClick, handleDelete })}
							</Box>
						)}
					</Box>
					{showTitle && (itemTitle || uploadedFileName) && (
						<Box className={styles.itemTitleContainer}>
							<Typography className={styles.itemTitle}>{itemTitle || uploadedFileName}</Typography>
							{itemDescription && (
								<Typography className={styles.itemDescription}>{itemDescription}</Typography>
							)}
						</Box>
					)}
				</>
			) : (
				<Box
					className={clsx(emptyStateClassName, styles.emptyState, {
						[styles.horizontalLayout]: horizontalLayout,
					})}
				>
					<Box
						className={clsx(styles.imageWrapper, {
							[styles.imagePreview]: showImagePreview,
							[styles.errorBorder]: highlightError,
						})}
					>
						{horizontalLayout && showImagePreview && (
							<Box
								className={clsx(styles.imgContainer, {
									[styles.horizontalLayout]: horizontalLayout,
								})}
							>
								{!customOverlay && !loading && (
									<BriaIconButton className={styles.deleteIconBtn} onClick={handleDelete}>
										<DeleteIcon />
									</BriaIconButton>
								)}
								<LazyLoadImage
									className={styles.img}
									src={previewSrc}
									loading="lazy"
									threshold={100}
									effect="blur"
								/>
								{showErrorOverlay && (
									<Box className={styles.errorOverlay}>
										<WarningAlertIcon width={40} height={40} />
									</Box>
								)}
								{renderUploadingOverlay()}
								{customOverlay && !loading && (
									<Box className={clsx(styles.customOverlay, customOverlayClassName)}>
										{customOverlay({ handleUploadClick, handleDelete })}
									</Box>
								)}
							</Box>
						)}
						{!src &&
							showIcon &&
							(icon ? (
								<Box
									className={clsx(styles.image, {
										[styles.largeIcon]: largeIcon,
										[styles.smallIcon]: smallIcon,
									})}
								>
									{icon}
								</Box>
							) : (
								<ImageIcon />
							))}
					</Box>

					<Box className={clsx(styles.contentWrapper, contentWrapperClassName)}>
						<Box className={clsx(styles.description, titleClassName)}>{description}</Box>
						{optional && <Box className={clsx(styles.optional)}>Optional</Box>}
						{!hideSupportElement && (
							<Box className={clsx(styles.support, descriptionClassName)}>
								{supportedFilesText
									? supportedFilesText
									: `${t("emptyState.supportedFiles")} ${supportedFiles}`}
							</Box>
						)}
						{inlineError && <Box className={styles.error}>{error}</Box>}
						{children}
					</Box>
					{renderImageLimitText()}
					{renderUploadedImagesRow()}
				</Box>
			)}
		</Box>
	);
};

export default observer(ImageUploader);
